import { useEffect as useDrawerBaselineEffect, useEffectEvent as useDrawerBaselineEvent } from "react";
import { useDirtySnapshot } from "../../../../../../../shared/frontend/forms/useDirtySnapshot";
import { formSnapshot } from "../../../../../../../shared/frontend/forms/formSnapshot";
import { borderRadius, spacing } from "../../../app/design-system";
import { CreditCardOutlined, EditOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Col, Descriptions, Form, InputNumber, Row, Select, theme, Typography } from "../../../../../../../shared/frontend/ui";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import {
  SURCHARGE_TYPE_FIXED_PER_INSTALLMENT,
  SURCHARGE_TYPE_PERCENT_OF_TOTAL
} from "../../../app/constants/paymentPlanSurchargeType";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly, formatMoney, formatPercent } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { formatCycleUnitLabel } from "../../../app/utils/cycleUnitI18n";
import { paymentMethodDefinitionSelectOptions, paymentMethodLabel } from "../../../app/utils/paymentMethod";
import { PAYMENT_METHOD_LABELS } from "../../../app/utils/paymentMethodCodes";
import { DrawerSelectionList } from "../../../components/DrawerSelectionList";
import { FormDrawer } from "../../../components/FormDrawer";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import { isContributionOpenItemActionEligible } from "./personContributionEligibility";
import { PERSON_DETAIL_LOOKUP_STALE_TIME_MS, personDetailQueryKeys } from "./personDetailQueryKeys";
import type { ContributionCalendarRow, PaymentPlanDto } from "./personDetailTypes";
import { computeInstallmentsPreviewWithRounding, formatDateOnlyIso } from "./personDetailUtils";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const SURCHARGE_PERCENT_DECIMALS = 2;
const DEFAULT_INSTALLMENT_COUNT = 2;

type InstallmentFormValues = {
  paymentPlanId: string | null;
  installmentCount: number | null;
  startDate: Dayjs | null;
};

type PaymentMethodDefinition = {
  paymentMethodType: number;
  name: string;
  isActive?: boolean;
};

type Props = {
  api: ApiClient;
  personId: string;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  dateInputFormat: string | string[];
  selectedRows: ContributionCalendarRow[];
  canAgreeInstallment: boolean;
  canWriteFinance: boolean;
  onChanged: () => void;
};

function contributionDescription(row: ContributionCalendarRow, text: I18nContextValue["text"]): string {
  return String(row.contractName || row.contractPlanName || "").trim() || text("Contribution");
}

export function PersonContributionOpenItemActions({
  api,
  personId,
  text,
  units,
  dateInputFormat,
  selectedRows,
  canAgreeInstallment,
  canWriteFinance,
  onChanged
}: Props) {
  const queryClient = useQueryClient();
  const feedback = useFeedback();
  const { token } = theme.useToken();
  const [eligibleRows, setEligibleRows] = React.useState<ContributionCalendarRow[]>([]);

  const [installmentOpen, setInstallmentOpen] = React.useState(false);
  const [installmentSaving, setInstallmentSaving] = React.useState(false);
  const [installmentRows, setInstallmentRows] = React.useState<ContributionCalendarRow[]>([]);
  const [installmentForm] = Form.useForm<InstallmentFormValues>();
  const installmentFields = Form.useWatch(formSnapshot, { form: installmentForm, preserve: true });
  const { dirty: installmentDirty, markSaved: installmentMarkSaved } = useDirtySnapshot({ fields: installmentFields, extra: { selected: installmentRows.map(row => row.id) } });
  const installmentInitializeDirty = useDrawerBaselineEvent(() => installmentMarkSaved({ fields: formSnapshot(installmentForm.getFieldsValue(true)), extra: { selected: installmentRows.map(row => row.id) } }));
  useDrawerBaselineEffect(() => { if (installmentOpen) installmentInitializeDirty(); }, [installmentOpen]);

  const watchedPaymentPlanId = Form.useWatch("paymentPlanId", installmentForm);
  const watchedInstallmentCount = Form.useWatch("installmentCount", installmentForm);
  const watchedStartDate = Form.useWatch("startDate", installmentForm);
  const [paymentPlans, setPaymentPlans] = React.useState<PaymentPlanDto[]>([]);

  const [paymentMethodOpen, setPaymentMethodOpen] = React.useState(false);
  const [paymentMethodSaving, setPaymentMethodSaving] = React.useState(false);
  const [paymentMethodRows, setPaymentMethodRows] = React.useState<ContributionCalendarRow[]>([]);
  const [paymentMethodForm] = Form.useForm<{ paymentMethod?: number }>();
  const paymentMethodFields = Form.useWatch(formSnapshot, { form: paymentMethodForm, preserve: true });
  const { dirty: paymentMethodDirty, markSaved: paymentMethodMarkSaved } = useDirtySnapshot({ fields: paymentMethodFields, extra: { selected: paymentMethodRows.map(row => row.id) } });
  const paymentMethodInitializeDirty = useDrawerBaselineEvent(() => paymentMethodMarkSaved({ fields: formSnapshot(paymentMethodForm.getFieldsValue(true)), extra: { selected: paymentMethodRows.map(row => row.id) } }));
  useDrawerBaselineEffect(() => { if (paymentMethodOpen) paymentMethodInitializeDirty(); }, [paymentMethodOpen]);

  const watchedPaymentMethod = Form.useWatch("paymentMethod", paymentMethodForm);
  const [paymentMethodDefinitions, setPaymentMethodDefinitions] = React.useState<PaymentMethodDefinition[]>([]);

  const loadEligibleRows = React.useCallback(async () => {
    const rows = await queryClient.fetchQuery({
      queryKey: personDetailQueryKeys.person.contributions(personId),
      queryFn: () => api.get<ContributionCalendarRow[]>(`/api/v1/contribution-calendar?personId=${encodeURIComponent(personId)}`),
      staleTime: 0
    });
    return (rows || []).filter(isContributionOpenItemActionEligible);
  }, [api, personId, queryClient]);

  const selectedEligibleIds = React.useMemo(
    () => new Set(selectedRows.filter(isContributionOpenItemActionEligible).map((row) => String(row.id))),
    [selectedRows]
  );

  const openInstallment = React.useCallback(async () => {
    const [rows, plans] = await Promise.all([
      loadEligibleRows(),
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.lookups.paymentPlans,
        queryFn: async () => ((await api.get<PaymentPlanDto[]>("/api/v1/payment-plans")) || []).filter((plan) => plan.isActive),
        staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
      })
    ]);
    if (rows.length === 0) {
      feedback.warning(text("No open items found for installment plan."));
      return;
    }
    setEligibleRows(rows);
    setInstallmentRows(rows.filter((row) => selectedEligibleIds.has(String(row.id))));
    setPaymentPlans(plans);
    installmentForm.setFieldsValue({ paymentPlanId: null, installmentCount: null, startDate: dayjs().startOf("day") });
    setInstallmentOpen(true);
  }, [api, installmentForm, loadEligibleRows, feedback, queryClient, selectedEligibleIds, text]);

  const openPaymentMethod = React.useCallback(async () => {
    const [rows, definitions] = await Promise.all([
      loadEligibleRows(),
      api.get<PaymentMethodDefinition[]>("/api/v1/payment-method-definitions")
    ]);
    if (rows.length === 0) {
      feedback.warning(text("No open contributions to change payment method"));
      return;
    }
    const initialRows = rows.filter((row) => selectedEligibleIds.has(String(row.id)));
    const methods = new Set(initialRows.map((row) => Number(row.paymentMethod || 0)).filter((method) => method > 0));
    setEligibleRows(rows);
    setPaymentMethodRows(initialRows);
    setPaymentMethodDefinitions(definitions || []);
    paymentMethodForm.resetFields();
    paymentMethodForm.setFieldsValue({ paymentMethod: methods.size === 1 ? [...methods][0] : undefined });
    setPaymentMethodOpen(true);
  }, [api, loadEligibleRows, feedback, paymentMethodForm, selectedEligibleIds, text]);

  const selectedPlan = React.useMemo(() => {
    const id = watchedPaymentPlanId ? String(watchedPaymentPlanId) : "";
    return id ? paymentPlans.find((plan) => String(plan.id) === id) || null : null;
  }, [paymentPlans, watchedPaymentPlanId]);

  const installmentPreview = React.useMemo(() => {
    if (!selectedPlan || installmentRows.length === 0) return null;
    const basePrice = installmentRows.reduce((sum, row) => sum + Number(row.openAmount || 0), 0);
    const count = Number(watchedInstallmentCount || 0);
    if (!Number.isFinite(basePrice) || basePrice <= 0 || !Number.isFinite(count) || count < 1) return null;
    const preview = computeInstallmentsPreviewWithRounding(selectedPlan, basePrice, count);
    const surchargeType = Number(selectedPlan.surchargeType || SURCHARGE_TYPE_FIXED_PER_INSTALLMENT);
    const surchargeValue = Number(selectedPlan.surchargeValue || 0);
    let surchargeLabel = "—";
    if (count > 1 && surchargeValue > 0) {
      if (surchargeType === SURCHARGE_TYPE_FIXED_PER_INSTALLMENT) {
        surchargeLabel = formatMoney(surchargeValue, units) + text("per installment");
      } else if (surchargeType === SURCHARGE_TYPE_PERCENT_OF_TOTAL) {
        surchargeLabel = `${formatPercent(surchargeValue, units, SURCHARGE_PERCENT_DECIMALS)} ${text("of total")}`;
      }
    }
    return {
      ...preview,
      basePrice,
      surchargeLabel,
      cycle: count <= 1 ? text("Cycle unit once") : formatCycleUnitLabel(selectedPlan.cycleUnit, text)
    };
  }, [installmentRows, selectedPlan, text, units, watchedInstallmentCount]);

  const invalidateFinanceData = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.contributions(personId) });
    void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.openItems(personId) });
    void queryClient.invalidateQueries({ queryKey: ["person", personId, "open-item-installments"] });
    onChanged();
  }, [onChanged, personId, queryClient]);

  const saveInstallment = async () => {
    const values = await installmentForm.validateFields();
    if (installmentRows.length === 0) {
      feedback.warning(text("Please select at least one open item"));
      return;
    }
    const startDate = formatDateOnlyIso(values.startDate);
    if (!startDate) throw new Error(text("Start date is required"));
    setInstallmentSaving(true);
    try {
      for (const row of installmentRows) {
        await api.post(`/api/v1/open-items/${encodeURIComponent(String(row.openItemId))}/installment`, {
          paymentPlanId: values.paymentPlanId,
          installmentCount: values.installmentCount,
          startDate
        });
      }
      feedback.success(
        installmentRows.length === 1
          ? text("Installment plan created")
          : text("Installment plan created for items").replace("{0}", String(installmentRows.length))
      );
      setInstallmentOpen(false);
      invalidateFinanceData();
    } finally {
      setInstallmentSaving(false);
    }
  };

  const savePaymentMethod = async () => {
    const values = await paymentMethodForm.validateFields();
    if (paymentMethodRows.length === 0) {
      feedback.warning(text("Please select at least one open item"));
      return;
    }
    setPaymentMethodSaving(true);
    try {
      for (const row of paymentMethodRows) {
        await api.put(`/api/v1/contribution-calendar/${encodeURIComponent(row.id)}`, {
          paymentMethod: Number(values.paymentMethod)
        });
      }
      feedback.success(text("Payment method changed for contributions").replace("{0}", String(paymentMethodRows.length)));
      setPaymentMethodOpen(false);
      invalidateFinanceData();
    } finally {
      setPaymentMethodSaving(false);
    }
  };

  const paymentMethodNameByType = React.useMemo(() => {
    const names: Record<number, string> = {};
    for (const definition of paymentMethodDefinitions) {
      names[definition.paymentMethodType] = String(definition.name || "").trim()
        || PAYMENT_METHOD_LABELS[definition.paymentMethodType]
        || String(definition.paymentMethodType);
    }
    return names;
  }, [paymentMethodDefinitions]);

  return (
    <>
      {canAgreeInstallment ? (
        <Button icon={<CreditCardOutlined />} onClick={() => openInstallment().catch(() => {})}>
          {text("Installment plan")}
        </Button>
      ) : null}
      {canWriteFinance ? (
        <Button icon={<EditOutlined />} onClick={() => openPaymentMethod().catch(() => {})}>
          {text("Change payment method (open contributions)")}
        </Button>
      ) : null}

      {!installmentOpen ? <Form form={installmentForm} style={{ display: "none" }} /> : null}
      <FormDrawer dirty={installmentDirty}
        title={text("Create installment plan")}
        open={installmentOpen}
        onCancel={() => {
          setInstallmentOpen(false);
          setInstallmentRows([]);
          setEligibleRows([]);
        }}
        submitting={installmentSaving}
        submitDisabled={
          installmentRows.length === 0
          || !watchedPaymentPlanId
          || !watchedInstallmentCount
          || !watchedStartDate
        }
        onSave={() => saveInstallment().catch(() => {})}
      >
        <Form form={installmentForm} layout="vertical" requiredMark={false}>
          <DrawerSelectionList<ContributionCalendarRow>
            title={text("Select contributions")}
            dataSource={eligibleRows}
            rowKey="id"
            emptyText={<Typography.Text type="secondary">{text("No open items found for installment plan.")}</Typography.Text>}
            columns={[
              {
                title: text("Description"),
                dataIndex: "contractName",
                key: "description",
                render: (_, row) => contributionDescription(row, text)
              },
              {
                title: <span style={{ whiteSpace: "nowrap" }}>{text("Due date")}</span>,
                dataIndex: "dueDate",
                key: "dueDate",
                width: 120,
                render: (_, row) => formatDateOnly(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH), units)
              },
              {
                title: text("Open (status)"),
                dataIndex: "openAmount",
                key: "openAmount",
                width: 100,
                align: "right",
                render: (_, row) => formatMoney(Number(row.openAmount || 0), units)
              }
            ]}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: installmentRows.map((row) => row.id),
              onChange: (_keys, rows) => setInstallmentRows(rows as ContributionCalendarRow[]),
              hideSelectAll: false,
              selectionByRowClick: true
            }}
          />
          <Row gutter={spacing.lg}>
            <Col xs={24} sm={12}>
              <Form.Item name="paymentPlanId" label={text("Payment plan")} rules={[{ required: true, message: text("Required") }]}>
                <Select
                  style={{ width: "100%" }}
                  placeholder={text("Select plan")}
                  options={paymentPlans.map((plan) => ({ value: plan.id, label: plan.name }))}
                  onChange={() => installmentForm.setFieldValue("installmentCount", DEFAULT_INSTALLMENT_COUNT)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="installmentCount" label={text("Number of installments")} rules={[{ required: true, message: text("Required") }]}>
                <InputNumber controls style={{ width: "100%" }} min={2} max={24} step={1} />
              </Form.Item>
            </Col>
          </Row>
          {selectedPlan && installmentRows.length > 0 && installmentPreview ? (
            <Form.Item label={text("Preview")}>
              <div style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: borderRadius.xl, padding: spacing.md, background: token.colorFillAlter }}>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: spacing.sm }}>
                  {text("Financial breakdown")}
                </Typography.Text>
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "base",
                      label: installmentRows.length > 1 ? text("Base amount (selected open items)") : text("Base amount (open item)"),
                      children: formatMoney(installmentPreview.basePrice, units)
                    },
                    { key: "surcharge", label: text("Surcharge"), children: installmentPreview.surchargeLabel },
                    { key: "cycle", label: text("Payment cycle"), children: installmentPreview.cycle },
                    {
                      key: "installment",
                      label: installmentPreview.count > 1 ? text("Price per installment") : text("Price"),
                      children: formatMoney(installmentPreview.count > 1 ? installmentPreview.installment : installmentPreview.total, units)
                    },
                    { key: "total", label: text("Total"), children: formatMoney(installmentPreview.total, units) }
                  ]}
                />
              </div>
            </Form.Item>
          ) : null}
          <Form.Item name="startDate" label={text("Start date")} rules={[{ required: true, message: text("Required") }]}>
            <DatePicker allowClear={false} format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </FormDrawer>

      {!paymentMethodOpen ? <Form form={paymentMethodForm} style={{ display: "none" }} /> : null}
      <FormDrawer dirty={paymentMethodDirty}
        title={text("Change payment method (open contributions)")}
        open={paymentMethodOpen}
        onCancel={() => setPaymentMethodOpen(false)}
        submitting={paymentMethodSaving}
        submitDisabled={paymentMethodRows.length === 0 || !watchedPaymentMethod}
        onSave={() => savePaymentMethod().catch(() => {})}
      >
        <Form form={paymentMethodForm} layout="vertical" requiredMark={false}>
          <DrawerSelectionList<ContributionCalendarRow>
            title={text("Select contributions")}
            dataSource={eligibleRows}
            rowKey="id"
            emptyText={<Typography.Text type="secondary">{text("No open contributions to change payment method")}</Typography.Text>}
            columns={[
              {
                title: text("Description"),
                dataIndex: "contractName",
                key: "description",
                render: (_, row) => contributionDescription(row, text)
              },
              {
                title: <span style={{ whiteSpace: "nowrap" }}>{text("Due date")}</span>,
                dataIndex: "dueDate",
                key: "dueDate",
                width: 120,
                render: (_, row) => formatDateOnly(String(row.dueDate || "").slice(0, ISO_DATE_LENGTH), units)
              },
              {
                title: text("Open (status)"),
                dataIndex: "openAmount",
                key: "openAmount",
                width: 100,
                align: "right",
                render: (_, row) => formatMoney(Number(row.openAmount || 0), units)
              },
              {
                title: text("Payment method"),
                dataIndex: "paymentMethod",
                key: "paymentMethod",
                width: 140,
                render: (_, row) => paymentMethodLabel(Number(row.paymentMethod || 0), paymentMethodNameByType)
              }
            ]}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: paymentMethodRows.map((row) => row.id),
              onChange: (_keys, rows) => setPaymentMethodRows(rows as ContributionCalendarRow[]),
              hideSelectAll: false,
              selectionByRowClick: true
            }}
          />
          <Form.Item
            name="paymentMethod"
            label={text("New payment method")}
            rules={[{ required: true, message: text("Payment method is required") }]}
          >
            <Select
              options={paymentMethodDefinitionSelectOptions(paymentMethodDefinitions, true)}
              placeholder={text("Select payment method…")}
            />
          </Form.Item>
        </Form>
      </FormDrawer>
    </>
  );
}
